import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Cloud,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  Instagram,
  Linkedin,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings, pickCmsShort } from '../services/publicContentService';

// Static defaults — used until the admin edits `settings.contact_page`.
const DEFAULTS = {
  hero_title:      { ar: 'تواصل معنا',  en: 'Contact Us' },
  hero_subtitle:   { ar: 'يسعدنا التواصل معكم والإجابة على استفساراتكم. لا تترددوا في التواصل مع فريقنا.',
                     en: "We'd love to hear from you. Get in touch with our team and we'll respond as soon as possible." },
  phone:   '+966 012 6067393',
  email:   'info@ebe.com.sa',
  address: { ar: 'ص.ب 3500\nجدة 21471\nالمملكة العربية السعودية',
             en: 'P.O. Box 3500\nJeddah 21471\nKingdom of Saudi Arabia' },
  map_url:    'https://maps.app.goo.gl/mgePFeqRXXv7rf4u9?g_st=iw',
  social: {
    instagram: 'https://www.instagram.com/ebalance_sa',
    linkedin:  'https://www.linkedin.com/in/environmental-balance-88491a396?utm_source=share_via&utm_content=profile&utm_medium=member_ios',
    tiktok:    'https://vt.tiktok.com/ZSXVcBNxw/',
    x:         'https://x.com/ebalance_sa',
  },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small custom marks for TikTok + X (lucide doesn't ship these two).
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.86a8.16 8.16 0 0 0 4.77 1.52V7.94a4.86 4.86 0 0 1-1.84-1.25z" />
  </svg>
);
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2H21.5l-7.5 8.573L23 22h-6.844l-5.34-6.99L4.7 22H1.44l8.03-9.176L1 2h6.99l4.83 6.386L18.244 2Zm-1.2 18h1.84L7.05 3.9H5.09l11.954 16.1Z" />
  </svg>
);

// Small labeled input primitive
const Input = React.forwardRef(({ id, label, dir, error, required, ...rest }, ref) => (
  <div>
    <label htmlFor={id} className={`block text-xlfont-semibold text-slate-800 dark:text-slate-100 mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
      {label} {required && <span className="text-brand-blue-700">*</span>}
    </label>
    <input
      id={id}
      ref={ref}
      dir={dir}
      required={required}
      aria-invalid={!!error}
      className={`w-full px-4 py-3 rounded-xl bg-white/80 dark:bg-slate-900/50 backdrop-blur border ${
        error ? 'border-red-400 focus:ring-red-300' : 'border-slate-200 dark:border-slate-700 focus:ring-brand-blue/40'
      } text-xltext-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-shadow`}
      {...rest}
    />
    {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
  </div>
));
Input.displayName = 'ContactInput';

const initialForm = { name: '', email: '', phone: '', subject: '', message: '' };
const initialErrs = {};

const ContactPage = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [cms, setCms] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('contact_page').then((v) => { if (alive) setCms(v || {}); });
    return () => { alive = false; };
  }, []);

  // Resolve display values — CMS override → default
  const heroTitle    = pickCmsShort(cms.hero_title,    isAr) || (isAr ? DEFAULTS.hero_title.ar    : DEFAULTS.hero_title.en);
  const heroSubtitle = pickCmsShort(cms.hero_subtitle, isAr) || (isAr ? DEFAULTS.hero_subtitle.ar : DEFAULTS.hero_subtitle.en);
  const phone        = (cms.phone || '').trim() || DEFAULTS.phone;
  const email        = (cms.email || '').trim() || DEFAULTS.email;
  const address      = pickCmsShort(cms.address, isAr) || (isAr ? DEFAULTS.address.ar : DEFAULTS.address.en);
  const mapUrl       = (cms.map_url || '').trim() || DEFAULTS.map_url;
  const social       = { ...DEFAULTS.social, ...(cms.social || {}) };

  // Form state
  const [form, setForm] = useState(initialForm);
  const [errs, setErrs] = useState(initialErrs);
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const [statusMsg, setStatusMsg] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = isAr ? 'الاسم مطلوب' : 'Name is required';
    if (!form.email.trim())   e.email   = isAr ? 'البريد مطلوب' : 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
                              e.email   = isAr ? 'صيغة بريد غير صحيحة' : 'Invalid email';
    if (!form.subject.trim()) e.subject = isAr ? 'الموضوع مطلوب' : 'Subject is required';
    if (!form.message.trim()) e.message = isAr ? 'الرسالة مطلوبة' : 'Message is required';
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setStatus('sending'); setStatusMsg('');
    try {
      await axios.post(`${API}/contact/submit`, {
        name:    form.name.trim(),
        email:   form.email.trim(),
        phone:   form.phone.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setStatus('sent');
      setStatusMsg(isAr ? 'تم إرسال رسالتك بنجاح — سنعاود التواصل قريباً.' : 'Your message was sent successfully — we\'ll get back to you shortly.');
      setForm(initialForm);
      setErrs(initialErrs);
    } catch (err) {
      setStatus('error');
      const detail = err?.response?.data?.detail;
      setStatusMsg(typeof detail === 'string' ? detail : (isAr
        ? 'تعذّر إرسال الرسالة الآن. يرجى المحاولة لاحقاً أو مراسلتنا على البريد مباشرة.'
        : 'We couldn\'t send your message right now. Please try again later or email us directly.'));
    }
  };

  return (
    <div dir={isAr ? 'rtl' : 'ltr'}>
      {/* ==================== SKY THEME BACKGROUND ==================== */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1601297183305-6df142704ea2?w=2400&q=85')" }}
      />
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-sky-100/60 via-brand-blue-50/80 to-white/95 dark:from-slate-950/85 dark:via-brand-blue-900/80 dark:to-slate-950/95" />
      <div className="fixed top-[10%] left-[8%] w-[600px] h-[280px] rounded-full blur-[100px] bg-white/50 dark:bg-brand-blue-700/15 pointer-events-none -z-10"
           style={{ animation: 'cloud-drift 28s ease-in-out infinite' }} />
      <div className="fixed top-[40%] right-[5%] w-[550px] h-[260px] rounded-full blur-[100px] bg-sky-200/40 dark:bg-brand-blue-600/15 pointer-events-none -z-10"
           style={{ animation: 'cloud-drift 35s ease-in-out infinite reverse' }} />
      <div className="fixed bottom-[15%] left-[30%] w-[450px] h-[220px] rounded-full blur-[100px] bg-brand-blue-100/50 dark:bg-brand-blue-500/12 pointer-events-none -z-10"
           style={{ animation: 'cloud-drift 32s ease-in-out infinite', animationDelay: '4s' }} />

      {/* ==================== HERO ==================== */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Drifting cloud particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full blur-md"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${30 + Math.random() * 60}px`,
                height: `${10 + Math.random() * 25}px`,
                background: 'rgba(255,255,255,0.55)',
                animation: `cloud-float ${20 + Math.random() * 20}s linear infinite`,
                animationDelay: `${Math.random() * 20}s`,
              }}
            />
          ))}
        </div>
        <div className="relative container mx-auto px-4 text-center max-w-4xl animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/60 dark:bg-white/10 border border-brand-blue/30 mb-8 backdrop-blur-xl shadow-md">
            <Cloud className="w-4 h-4 text-brand-blue-700 dark:text-brand-blue-300" />
            <span className="text-xs tracking-[0.4em] uppercase text-brand-blue-800 dark:text-brand-blue-200 font-semibold" data-testid="contact-eyebrow">
              {isAr ? 'تواصل' : 'Get in touch'}
            </span>
          </div>
          <h1
            className="text-5xl md:text-7xl font-black tracking-tight mb-6 leading-[1.05]"
            style={{ filter: 'drop-shadow(0 4px 12px rgba(255,255,255,0.95)) drop-shadow(0 2px 4px rgba(255,255,255,0.8))' }}
            data-testid="contact-title"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-blue-700 via-brand-blue-800 to-slate-900">
              {heroTitle}
            </span>
          </h1>
          <p
            className="text-xlmd:text-xl text-slate-800 dark:text-slate-200 max-w-2xl mx-auto leading-loose font-medium"
            style={{ textShadow: '0 2px 8px rgba(255,255,255,0.9)' }}
            data-testid="contact-subtitle"
          >
            {heroSubtitle}
          </p>
        </div>
      </section>

      {/* ==================== MAIN CONTENT ==================== */}
      {/* -------- PRIMARY: Send Us a Message (full-width form) -------- */}
      <section className="relative py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <div className="rounded-3xl bg-white/85 dark:bg-slate-900/60 border border-white/60 dark:border-slate-800 backdrop-blur-xl p-6 md:p-10 shadow-xl shadow-brand-blue/5">
              <h2 className={`text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-2 ${isAr ? 'text-right' : 'text-left'}`}>
                {isAr ? 'أرسل لنا رسالة' : 'Send us a message'}
              </h2>
              <p className={`text-xltext-slate-600 dark:text-slate-400 mb-6 ${isAr ? 'text-right' : 'text-left'}`}>
                {isAr ? 'الحقول المعلّمة (*) إلزامية.' : 'Fields marked (*) are required.'}
              </p>

              {status === 'sent' && (
                <div data-testid="contact-success"
                     className="flex items-start gap-3 rounded-xl p-3 mb-5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-600 mt-0.5" />
                  <p className="text-xltext-emerald-800 dark:text-emerald-200 leading-relaxed">{statusMsg}</p>
                </div>
              )}
              {status === 'error' && (
                <div data-testid="contact-error"
                     className="flex items-start gap-3 rounded-xl p-3 mb-5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-600 mt-0.5" />
                  <p className="text-xltext-red-800 dark:text-red-200 leading-relaxed">{statusMsg}</p>
                </div>
              )}

              <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid="contact-form">
                <Input
                  id="contact-name" data-testid="contact-name"
                  label={isAr ? 'الاسم الكامل' : 'Full Name'} required dir={isAr ? 'rtl' : 'ltr'}
                  autoComplete="name" value={form.name} onChange={(e) => set('name', e.target.value)}
                  error={errs.name}
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    id="contact-email" data-testid="contact-email" type="email"
                    label={isAr ? 'البريد الإلكتروني' : 'Email Address'} required dir="ltr"
                    autoComplete="email" value={form.email} onChange={(e) => set('email', e.target.value)}
                    error={errs.email}
                  />
                  <Input
                    id="contact-phone" data-testid="contact-phone" type="tel"
                    label={isAr ? 'رقم الهاتف' : 'Phone Number'} dir="ltr"
                    autoComplete="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)}
                  />
                </div>
                <Input
                  id="contact-subject" data-testid="contact-subject"
                  label={isAr ? 'الموضوع' : 'Subject'} required dir={isAr ? 'rtl' : 'ltr'}
                  value={form.subject} onChange={(e) => set('subject', e.target.value)}
                  error={errs.subject}
                />
                <div>
                  <label htmlFor="contact-message" className={`block text-xlfont-semibold text-slate-800 dark:text-slate-100 mb-2 ${isAr ? 'text-right' : 'text-left'}`}>
                    {isAr ? 'الرسالة' : 'Message'} <span className="text-brand-blue-700">*</span>
                  </label>
                  <textarea
                    id="contact-message" data-testid="contact-message"
                    rows={5} dir={isAr ? 'rtl' : 'ltr'}
                    aria-invalid={!!errs.message}
                    value={form.message} onChange={(e) => set('message', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl bg-white/80 dark:bg-slate-900/50 backdrop-blur border ${
                      errs.message ? 'border-red-400 focus:ring-red-300' : 'border-slate-200 dark:border-slate-700 focus:ring-brand-blue/40'
                    } text-xltext-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-shadow resize-y`}
                  />
                  {errs.message && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errs.message}</p>}
                </div>

                <button
                  type="submit" disabled={status === 'sending'} data-testid="contact-submit"
                  className={`w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl
                            bg-gradient-to-r from-brand-blue to-brand-blue-700
                            hover:from-brand-blue-700 hover:to-brand-blue-800
                            text-white text-base font-semibold shadow-md
                            hover:shadow-lg hover:-translate-y-0.5 transition-all
                            disabled:opacity-60 disabled:hover:translate-y-0 disabled:cursor-not-allowed`}
                >
                  {status === 'sending'
                    ? (<><Loader2 className="w-5 h-5 animate-spin" /> {isAr ? 'جارٍ الإرسال…' : 'Sending…'}</>)
                    : (<><Send className="w-5 h-5" /> {isAr ? 'إرسال رسالة' : 'Send Message'}</>)}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* -------- SECONDARY: Map + Contact Information -------- */}
      <section className="relative pb-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-8 max-w-6xl mx-auto">
            {/* -------- LEFT: Larger Google Map (3/5) -------- */}
            <div className="lg:col-span-3 animate-fade-in-up">
              <div className="rounded-3xl overflow-hidden bg-white/85 dark:bg-slate-900/60 border border-white/60 dark:border-slate-800 backdrop-blur-xl shadow-xl shadow-brand-blue/5 h-full flex flex-col">
                <div className="w-full flex-1 min-h-[420px]">
                  <iframe
                    title={isAr ? 'موقعنا على الخريطة' : 'Our location on the map'}
                    src="https://www.google.com/maps?q=Jeddah,+Saudi+Arabia&output=embed"
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="contact-map"
                  />
                </div>
                <div className={`p-4 flex ${isAr ? 'justify-start' : 'justify-end'}`}>
                  <a href={mapUrl} target="_blank" rel="noopener noreferrer" data-testid="view-on-google-maps"
                     className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 hover:from-brand-blue-700 hover:to-brand-blue-800 text-white text-xlfont-semibold shadow hover:shadow-lg hover:-translate-y-0.5 transition-all ${isAr ? 'flex-row-reverse' : ''}`}>
                    <ExternalLink className="w-4 h-4" />
                    {isAr ? 'عرض على خرائط Google' : 'View on Google Maps'}
                  </a>
                </div>
              </div>
            </div>

            {/* -------- RIGHT: Contact info card with socials inside (2/5) -------- */}
            <div className="lg:col-span-2 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              <div className="rounded-3xl bg-white/85 dark:bg-slate-900/60 border border-white/60 dark:border-slate-800 backdrop-blur-xl p-6 md:p-8 shadow-xl shadow-brand-blue/5 h-full">
                <h2 className={`text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-6 ${isAr ? 'text-right' : 'text-left'}`}>
                  {isAr ? 'معلومات التواصل' : 'Contact Information'}
                </h2>
                <ul className="space-y-5">
                  <ContactRow icon={Phone}  href={`tel:${phone.replace(/\s+/g, '')}`}
                              label={isAr ? 'الهاتف' : 'Phone'} value={phone} isAr={isAr} testId="info-phone" />
                  <ContactRow icon={Mail}   href={`mailto:${email}`}
                              label={isAr ? 'البريد الإلكتروني' : 'Email'} value={email} isAr={isAr} testId="info-email" />
                  <ContactRow icon={MapPin} label={isAr ? 'العنوان' : 'Address'}
                              value={<span className="whitespace-pre-line">{address}</span>}
                              isAr={isAr} testId="info-address" />
                </ul>

                {/* Divider */}
                <div className="my-6 h-px bg-gradient-to-r from-transparent via-brand-blue/20 to-transparent" />

                {/* Socials inside the same card */}
                <h3 className={`text-xlfont-bold uppercase tracking-widest text-brand-blue-800 dark:text-brand-blue-300 mb-4 ${isAr ? 'text-right' : 'text-left'}`}>
                  {isAr ? 'تابعنا' : 'Follow us'}
                </h3>
                <div className={`flex flex-wrap gap-3 ${isAr ? 'justify-end' : 'justify-start'}`}>
                  <SocialLink href={social.instagram} label="Instagram" testId="social-instagram" Icon={Instagram} />
                  <SocialLink href={social.linkedin}  label="LinkedIn"  testId="social-linkedin"  Icon={Linkedin} />
                  <SocialLink href={social.tiktok}    label="TikTok"    testId="social-tiktok"    Icon={TikTokIcon} />
                  <SocialLink href={social.x}         label="X (Twitter)" testId="social-x"       Icon={XIcon} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-16" />
    </div>
  );
};

/* Row inside the "Contact Information" card. */
const ContactRow = ({ icon: Icon, href, label, value, isAr, testId }) => {
  const inner = (
    <>
      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-brand-blue-50 to-sky-100 dark:from-brand-blue-900 dark:to-slate-900 border border-brand-blue/30 flex items-center justify-center shadow-sm">
        <Icon className="w-5 h-5 text-brand-blue-700 dark:text-brand-blue-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-base md:text-lg uppercase tracking-widest text-brand-blue-800 dark:text-brand-blue-300 font-semibold mb-0.5">
          {label}
        </div>
        <div className="text-base md:text-lg text-slate-900 dark:text-white break-words leading-relaxed" style={{ direction: 'ltr' }}>
          {value}
        </div>
      </div>
    </>
  );
  const cls = `group flex items-start gap-4 ${isAr ? 'flex-row-reverse text-right' : 'text-left'}`;
  return (
    <li data-testid={testId}>
      {href
        ? <a href={href} className={`${cls} hover:opacity-90 transition-opacity`}>{inner}</a>
        : <div className={cls}>{inner}</div>}
    </li>
  );
};

/* Circular social button with hover animation. */
const SocialLink = ({ href, label, testId, Icon }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} data-testid={testId}
     className="group relative w-11 h-11 rounded-xl bg-gradient-to-br from-brand-blue-50 to-sky-100 dark:from-brand-blue-900 dark:to-slate-900 border border-brand-blue/30 flex items-center justify-center text-brand-blue-700 dark:text-brand-blue-300 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:from-brand-blue hover:to-brand-blue-700 hover:text-white hover:border-transparent transition-all duration-300">
    <Icon className="w-5 h-5" />
  </a>
);

export default ContactPage;
